import type { NavSectionProps } from 'src/components/nav-section';

import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faGearApi, faBroomWide, faUserGroup } from '@fortawesome/pro-regular-svg-icons';

import { paths } from 'src/routes/paths';

import { useAdminPermissionsQuery } from 'src/hooks/admin';

import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';

import { useAuthContext } from 'src/auth/hooks';

import { Label } from '../components/label';
import { Role } from '../enums/auth/role.enum';

export type NavDataResult = {
  data: NavSectionProps['data'];
  error: Error | undefined;
};

export function useNavData(): NavDataResult {
  const { t } = useTranslate('navbar');
  const { assumedRoleId } = useAuthContext();
  const { permissions, error } = useAdminPermissionsQuery();

  const data = useMemo(() => {
    const adminItems = [];

    if (assumedRoleId && permissions?.canAccessUserManagement) {
      adminItems.push({
        title: t('administration.userManagement'),
        path: paths.admin.userManagement,
        icon: <FontAwesomeIcon icon={faUser} />,
        info: (
          <Label color="info" variant="soft" sx={{ fontSize: 10, height: 16 }}>
            {t('common.new')}
          </Label>
        ),
      });
    }

    if (assumedRoleId && permissions?.canAccessGroupManagement) {
      adminItems.push({
        title: t('administration.groupManagement'),
        path: paths.admin.groupManagement,
        icon: <FontAwesomeIcon icon={faUserGroup} />,
        info: (
          <Label color="info" variant="soft" sx={{ fontSize: 10, height: 16 }}>
            {t('common.new')}
          </Label>
        ),
      });
    }

    const sections = [];

    /**
     * Admin
     */
    if (adminItems.length > 0) {
      sections.push({
        subheader: t('administration.title'),
        items: adminItems,
      });
    }

    return [
      ...sections,
      /**
       * Superadmin
       */
      {
        subheader: t('superadmin.title'),
        allowedRoles: [Role.SuperAdmin],
        items: [
          // Swagger UI is currently dev-only due to vulnerabilities in swagger-ui-react
          ...(import.meta.env.DEV && CONFIG.swagger.enabled
            ? [
                {
                  title: t('superadmin.swaggerApiDocumentation'),
                  path: paths.superadmin.swaggerApiDocumentation,
                  icon: <FontAwesomeIcon size="sm" icon={faGearApi} />,
                },
              ]
            : []),
          {
            title: t('superadmin.userManagement'),
            path: paths.superadmin.userManagement,
            icon: <FontAwesomeIcon icon={faUser} />,
          },
          {
            title: t('superadmin.groupManagement'),
            path: paths.superadmin.groupManagement,
            icon: <FontAwesomeIcon icon={faUserGroup} />,
          },
          {
            title: t('superadmin.userCleanup'),
            path: paths.superadmin.userCleanup,
            icon: <FontAwesomeIcon icon={faBroomWide} />,
          },
        ],
      },
    ];
  }, [t, assumedRoleId, permissions]);

  return { data, error };
}
